"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Mail, ArrowLeft } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
interface PolicySection {
  id: string;
  number: string;
  title: string;
  content: string[];
}
const policySections: PolicySection[] = [{
  id: "responsable",
  number: "01",
  title: "Identidad y domicilio del responsable",
  content: ["Spora, con domicilio de operaciones en Ciudad de México, México, y portal web oficial en spora.mx, es responsable del uso, tratamiento y protección de sus datos personales conforme a lo dispuesto en la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) y su Reglamento.", "Nuestro compromiso es garantizar la privacidad, integridad y confidencialidad de la información de todos nuestros clientes, visitantes y miembros de nuestra comunidad de bienestar botánico."]
}, {
  id: "datos-recabados",
  number: "02",
  title: "Datos personales que recabamos",
  content: ["Para llevar a cabo las finalidades descritas en el presente aviso, recabamos las siguientes categorías de datos personales:", "• Datos de identificación y contacto: nombre completo, correo electrónico, número telefónico y dirección postal para entrega.", "• Datos fiscales y de facturación: Registro Federal de Contribuyentes (RFC), razón social y domicilio fiscal (en caso de requerir Comprobante Fiscal Digital por Internet).", "• Datos de compra y transacciones: historial de pedidos, productos adquiridos y preferencias de compra.", "Importante sobre datos de pago: La información financiera y de tarjetas bancarias es procesada de manera directa y cifrada por nuestras pasarelas de pago certificadas (como Stripe y PayPal). Spora no almacena ni tiene acceso directo a los números completos de tarjeta ni códigos de seguridad."]
}, {
  id: "finalidades",
  number: "03",
  title: "Finalidades del tratamiento de datos",
  content: ["Sus datos personales serán utilizados para las siguientes finalidades primarias, necesarias para el servicio solicitado:", "1. Procesamiento, confirmación y envío de pedidos de productos de belleza y bienestar.", "2. Emisión de facturas y comprobantes fiscales correspondientes.", "3. Notificaciones sobre el estado de entrega y seguimiento logístico.", "4. Atención a clientes, aclaraciones, devoluciones y soporte técnico.", "De manera adicional, utilizaremos su información para finalidades secundarias que nos permiten brindarle una mejor atención:", "• Envío de nuestro boletín educativo sobre hongos funcionales, lanzamientos y promociones exclusivas.", "• Encuestas de satisfacción y evaluación de calidad de formulaciones.", "En caso de que no desee que sus datos personales sean tratados para estas finalidades secundarias, puede solicitarlo en cualquier momento enviando un correo a contacto@spora.beauty o mediante el enlace de cancelación incluido en cada comunicación electrónica."]
}, {
  id: "transferencias",
  number: "04",
  title: "Transferencia de datos personales",
  content: ["Sus datos personales pueden ser transferidos dentro y fuera del territorio nacional únicamente a:", "• Empresas de paquetería y logística autorizadas para la entrega física de sus pedidos.", "• Procesadores de pago electrónicos para la validación y liquidación de transacciones comerciales.", "• Autoridades competentes en los casos legalmente previstos por la legislación mexicana aplicable.", "Estas entidades operan bajo estrictas políticas de confidencialidad y únicamente tienen acceso a la información estrictamente necesaria para cumplir con los servicios encomendados."]
}, {
  id: "derechos-arco",
  number: "05",
  title: "Derechos ARCO y cómo ejercerlos",
  content: ["Usted tiene derecho a conocer qué datos personales tenemos de usted, para qué los utilizamos y las condiciones del uso que les damos (Acceso). Asimismo, es su derecho solicitar la corrección de su información en caso de que esté desactualizada, sea inexacta o incompleta (Rectificación); que la eliminemos de nuestros registros cuando considere que no está siendo utilizada adecuadamente (Cancelación); así como oponerse al uso de sus datos para fines específicos (Oposición). Estos se conocen como Derechos ARCO.", "Para el ejercicio de cualquiera de los derechos ARCO, deberá presentar la solicitud respectiva a través de correo electrónico a contacto@spora.beauty, indicando su nombre completo, copia de identificación oficial, descripción clara de los datos sobre los que busca ejercer el derecho y el tipo de derecho que desea promover.", "Daremos respuesta a su solicitud en un plazo máximo de 20 días hábiles a partir de la recepción de su correo."]
}, {
  id: "cookies",
  number: "06",
  title: "Uso de cookies y tecnologías de rastreo",
  content: ["Le informamos que en nuestra página de internet utilizamos cookies, web beacons y otras tecnologías a través de las cuales es posible monitorear su comportamiento como usuario de internet, brindarle un mejor servicio y experiencia de navegación al interactuar con nuestra tienda en línea.", "Los datos personales que obtenemos de estas tecnologías de rastreo incluyen identificadores de sesión, tipo de navegador, páginas visitadas y tiempo de navegación. Estas herramientas pueden ser deshabilitadas directamente en la configuración de privacidad de su navegador web."]
}, {
  id: "cambios",
  number: "07",
  title: "Cambios y actualizaciones al aviso de privacidad",
  content: ["El presente aviso de privacidad puede sufrir modificaciones, cambios o actualizaciones derivadas de nuevos requerimientos legales, de nuestras propias necesidades por los productos o servicios que ofrecemos, de nuestras prácticas de privacidad o de cambios en nuestro modelo de negocio.", "Nos comprometemos a mantenerlo informado sobre los cambios que pueda sufrir el presente aviso de privacidad a través de la publicación de la versión actualizada en esta misma sección de nuestro sitio web."]
}];
export default function Privacy() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="privacypolicy" data-nav="light" className="w-full bg-background text-foreground min-h-screen py-16 md:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Navigation back */}
        <div className="mb-8">
          <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground -ml-2 text-sm font-medium transition-colors">
            <Link to="/HomePage">
              <ArrowLeft className="size-4 mr-1.5" />
              Volver al inicio
            </Link>
          </Button>
        </div>

        {/* Header */}
        <motion.header initial={shouldReduceMotion ? false : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <ShieldCheck className="size-3.5" />
            <span>Marco legal LFPDPPP México</span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground mb-4">
            Aviso de Privacidad
          </h1>

          <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-xs sm:text-sm text-muted-foreground font-default">
            <span>Spora Bienestar Botánico S.A.S. de C.V.</span>
            <span className="hidden sm:inline" aria-hidden="true">•</span>
            <span>Última actualización: 24 de marzo de 2025</span>
          </div>

          <Separator className="mt-8 bg-border" />
        </motion.header>

        {/* Introduction note */}
        <div className="rounded-xl border border-border bg-card p-6 sm:p-7 mb-10 text-sm text-foreground/90 leading-relaxed font-default shadow-xs">
          <p>
            En Spora valoramos y respetamos la confianza que depositas en nosotros al elegir nuestros productos de cuidado facial, corporal y bienestar con hongos funcionales. Este aviso detalla con absoluta claridad cómo recopilamos, utilizamos y protegemos tu información personal de acuerdo con la legislación mexicana.
          </p>
        </div>

        {/* Policy Body */}
        <div className="space-y-10 font-default text-foreground/90">
          {policySections.map((section, index) => <motion.article key={section.id} data-index={index} initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-40px"
        }} transition={{
          duration: 0.4,
          delay: index * 0.04
        }} className="group">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="text-xs font-mono font-semibold text-primary">
                  {section.number}
                </span>
                <h2 className="font-serif text-xl sm:text-2xl font-medium text-foreground tracking-tight">
                  {section.title}
                </h2>
              </div>

              <div className="space-y-3 pl-7 text-sm sm:text-base leading-relaxed text-foreground/80">
                {section.content.map((paragraph, pIndex) => <p key={pIndex} className="whitespace-pre-line">
                    {paragraph}
                  </p>)}
              </div>

              {index < policySections.length - 1 && <Separator className="mt-8 bg-border/60" />}
            </motion.article>)}
        </div>

        {/* Contact and ARCO Card */}
        <div className="mt-14 rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div>
              <h3 className="font-serif text-lg font-semibold text-card-foreground mb-1">
                ¿Dudas sobre el tratamiento de tus datos?
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground font-default">
                Escríbenos directamente a nuestro departamento de privacidad para cualquier consulta o solicitud de derechos ARCO.
              </p>
            </div>

            <Button asChild variant="default" size="default" className="shrink-0 font-default">
              <a href="mailto:contacto@spora.beauty">
                <Mail className="size-4 mr-2" />
                contacto@spora.beauty
              </a>
            </Button>
          </div>
        </div>

        {/* Bottom Navigation */}
        <div className="mt-12 pt-8 border-t border-border flex flex-wrap justify-between items-center gap-4 text-xs text-muted-foreground font-default">
          <p>© {new Date().getFullYear()} Spora. Todos los derechos reservados.</p>
          <div className="flex items-center gap-4">
            <Link to="/ShopPage" className="text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline">
              Tienda
            </Link>
            <Link to="/HomePage" className="text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline">
              Inicio
            </Link>
          </div>
        </div>
      </div>
    </section>;
}